import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api/master-data';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    briefcase: ['M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16', 'M2 21h20', 'M8 7h8'],
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    alertTriangle: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
};

const TABS = [
    { key: 'client-industry', label: 'Industries', iconKey: 'briefcase', description: 'Configure industry types and client classifications.' },
];

function ItemModal({ tab, initial, onClose, onSave }) {
    const isEdit = !!initial;
    const [form, setForm] = useState(initial ? { ...initial } : { name: '', active: true });

    const handleSave = () => {
        if (!form.name.trim()) return;
        onSave(form);
        onClose();
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h2 className="md-modal-title">{isEdit ? 'Edit Industry' : 'Add Industry'}</h2>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={16} /></button>
                </div>
                <div className="md-modal-body">
                    <div className="md-form-group">
                        <label className="md-label">Industry Name<span className="md-required">*</span></label>
                        <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Technology" />
                    </div>
                    <div className="md-status-card">
                        <div className="md-status-card-info">
                            <span className="md-status-card-label">Status</span>
                            <span className="md-status-card-desc">{form.active ? 'Visible in client portal.' : 'Currently hidden.'}</span>
                        </div>
                        <button className={`md-toggle${form.active ? ' md-toggle-on' : ''}`} onClick={() => setForm({ ...form, active: !form.active })}>
                            <span className="md-toggle-thumb" />
                        </button>
                    </div>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-save-btn" onClick={handleSave} style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', borderRadius: 10 }}>
                        {isEdit ? 'Update Industry' : 'Add Industry'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DeleteModal({ itemName, onClose, onConfirm }) {
    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal md-modal-sm" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h2 className="md-modal-title">Confirm Deletion</h2>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={16} /></button>
                </div>
                <div className="md-modal-body md-delete-body">
                    <div className="md-delete-icon"><Icon d={icons.alertTriangle} size={28} /></div>
                    <p className="md-delete-msg">Delete <strong>"{itemName}"</strong>?<br /><span className="md-delete-sub">Action cannot be undone.</span></p>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-delete-btn" onClick={() => { onConfirm(); onClose(); }}>Delete Forever</button>
                </div>
            </div>
        </div>
    );
}

export default function ClientSettings() {
    const [activeTab, setActiveTab] = useState('client-industry');
    const [search, setSearch] = useState('');
    const [industries, setIndustries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API);
            setIndustries(res.data.filter(i => i.category === 'client-industry'));
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (formData) => {
        try {
            if (editItem) {
                const res = await axios.put(`${API}/${editItem._id}`, formData);
                setIndustries(prev => prev.map(i => i._id === editItem._id ? res.data : i));
            } else {
                const res = await axios.post(API, { ...formData, category: 'client-industry' });
                setIndustries(prev => [res.data, ...prev]);
            }
            setModalOpen(false);
            setEditItem(null);
        } catch (err) {
            console.error("Save error:", err);
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/${deleteItem._id}`);
            setIndustries(prev => prev.filter(i => i._id !== deleteItem._id));
            setDeleteItem(null);
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    const activeList = activeTab === 'client-industry' ? industries : [];
    const filteredItems = activeList.filter(i => (i.name || '').toLowerCase().includes(search.toLowerCase()));

    const currentTabObj = TABS.find(t => t.key === activeTab);

    return (
        <div className="ts-container">
            {/* Sidebar */}
            <aside className="ts-sidebar">
                <div className="ts-nav-item active" style={{ marginBottom: 24, cursor: 'default', background: 'none', color: '#1e293b', paddingLeft: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.5 }}>Config Categories</span>
                </div>
                {TABS.map(tab => (
                    <div
                        key={tab.key}
                        className={`ts-nav-item ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        <Icon d={icons[tab.iconKey]} size={18} />
                        <span>{tab.label}</span>
                    </div>
                ))}
            </aside>

            {/* Main Content */}
            <main className="ts-main">
                <header className="ts-header">
                    <div className="ts-title-group">
                        <h1>{currentTabObj.label}</h1>
                        <p>{currentTabObj.description}</p>
                    </div>
                    <div className="ts-controls">
                        <div className="md-search-wrap" style={{ margin: 0 }}>
                            <Icon d={icons.search} size={14} />
                            <input className="md-search" placeholder="Quick search..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <button className="md-add-btn" onClick={() => { setEditItem(null); setModalOpen(true); }} style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
                            <Icon d={icons.plus} size={14} /> Add {activeTab === 'client-industry' ? 'Industry' : 'Item'}
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading configurations...</div>
                ) : (
                    <div className="ts-grid">
                        {filteredItems.length === 0 ? (
                            <div style={{ gridColumn: '1 / -1', padding: '60px 0', textAlign: 'center', color: '#94a3b8' }}>
                                <Icon d={icons.briefcase} size={48} strokeWidth={1} />
                                <p style={{ marginTop: 16 }}>No items found in this category.</p>
                            </div>
                        ) : (
                            filteredItems.map(item => (
                                <div key={item._id} className="ts-priority-card tinted" style={{ borderColor: '#6366f1' }}>
                                    <div className="ts-priority-accent" style={{ background: '#6366f1' }} />
                                    <div className="ts-priority-header">
                                        <span className="ts-priority-name">{item.name}</span>
                                        <span className={`md-status-badge ${item.active ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                            {item.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className="ts-card-actions" style={{ border: 'none', marginTop: 12, paddingTop: 0, justifyContent: 'flex-end' }}>
                                        <button className="ts-action-btn" onClick={() => { setEditItem(item); setModalOpen(true); }}><Icon d={icons.edit} size={14} /></button>
                                        <button className="ts-action-btn danger" onClick={() => setDeleteItem(item)}><Icon d={icons.trash} size={14} /></button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </main>

            {modalOpen && (
                <ItemModal tab={currentTabObj} initial={editItem} onClose={() => setModalOpen(false)} onSave={handleSave} />
            )}

            {deleteItem && (
                <DeleteModal itemName={deleteItem.name} onClose={() => setDeleteItem(null)} onConfirm={handleDelete} />
            )}
        </div>
    );
}

